import { createRootRouteWithContext, Outlet, useLocation } from '@tanstack/react-router'
import MainLayout from '@/components/Common/MainLayout'
import { useAuthStore } from '@/stores/auth'
import GuestLayout from '@/components/Common/GuestLayout'
import FullScreenLayout from '@/components/Common/FullScreenLayout'
import { RouterContext } from '@/routerContext'
import PageShell from '@/components/Common/PageShell'
import ErrorState from '@/components/Common/ErrorState'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { DOCUMENTATION_NAV_STORAGE_KEY } from '@/components/Documentation/constants'
import { useWindowExports } from '@/hooks/useWindowExports'
import { useRecordPageVisit } from '@/hooks/useRecordPageVisit'
import i18next from 'i18next'

const AUTH_STORAGE_KEY = 'city-sense.auth'

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  errorComponent: ({ error }) => (
    <PageShell title={i18next.t('error', { ns: 'common' })} fullWidth>
      <ErrorState title={i18next.t('oopsSomethingWentWrong', { ns: 'common' })} message={error.message} />
    </PageShell>
  ),
})

function RootComponent() {
  const accessToken = useAuthStore(state => state.accessToken)
  const issuedAt = useAuthStore(state => state.issuedAt)
  const expiresIn = useAuthStore(state => state.expiresIn)
  const isLoading = useAuthStore(state => state.isLoading)
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)

  const location = useLocation()
  const navigate = useNavigate()

  usePageTitle()

  useWindowExports()

  useRecordPageVisit()

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === AUTH_STORAGE_KEY) {
        useAuthStore.persist.rehydrate()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  useEffect(() => {
    if (location.pathname !== '/login' && !isLoading) {
      if (!isAuthenticated()) {
        if (location.pathname === '/car-navigation') {
          return
        }

        navigate({
          to: '/login',
          search: {
            redirect: location.pathname,
          },
        })
      }
    }
  }, [accessToken, issuedAt, expiresIn, location.pathname, isLoading, isAuthenticated, navigate])

  if (location.pathname === '/login' && isLoading) {
    return (
      <GuestLayout>
        <Outlet />
      </GuestLayout>
    )
  }

  if (!isAuthenticated()) {
    return (
      <GuestLayout>
        <Outlet />
      </GuestLayout>
    )
  }

  const isHardwarePage = location.pathname.startsWith('/hardware')
  const isCarNavigationPage = location.pathname.includes('/car-navigation')
  const isRoutePlannerPage = location.pathname.startsWith('/route-planner')
  const isDocumentationPage = location.pathname === '/documentation' || location.pathname.startsWith('/documentation/')

  if (isHardwarePage) {
    return (
      <FullScreenLayout>
        <Outlet />
      </FullScreenLayout>
    )
  }

  if (isCarNavigationPage) {
    return (
      <FullScreenLayout>
        <Outlet />
      </FullScreenLayout>
    )
  }

  if (isRoutePlannerPage) {
    return (
      <FullScreenLayout>
        <Outlet />
      </FullScreenLayout>
    )
  }

  if (isDocumentationPage) {
    return (
      <MainLayout collapsed padding={0} navigationStorageKey={DOCUMENTATION_NAV_STORAGE_KEY}>
        <Outlet />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <Outlet />
    </MainLayout>
  )
}

export default RootComponent
